/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Banner logic module.
 * Handles CRUD operations for promotional banners displayed on the platform.
 * @module logic/banner
 */

import { refreshToken, signOut } from "../../utils/common.js";

/**
 * Fetches all banners from the API.
 * @async
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Array>} Array of banner objects or empty array on error
 */
export const getBanners = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}banner`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getBanners(navigate);
    }

    return data.data;
  } catch (error) {
    logger.error("Error fetching banners:", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Creates a new banner.
 * @async
 * @param {Object} bannerData - Banner data to create
 * @param {string} bannerData.redirectUrl - URL to redirect when banner is clicked
 * @param {string} bannerData.bannerImageFileName - Filename of the banner image
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Object|null>} Created banner data or null on error
 */
export const createBanner = async (bannerData, navigate) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(bannerData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}banner`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await createBanner(bannerData, navigate);
    }

    return data;
  } catch (error) {
    logger.error("Error creating banner:", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Updates an existing banner.
 * @async
 * @param {string} bannerId - The banner's unique identifier
 * @param {Object} bannerData - Updated banner data
 * @param {string} bannerData.redirectUrl - URL to redirect when banner is clicked
 * @param {boolean} bannerData.isNewBannerImageUploaded - Whether a new image was uploaded
 * @param {string} [bannerData.bannerImageFileName] - New banner image filename (if uploaded)
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<Object|null>} Updated banner data or null on error
 */
export const updateBanner = async (bannerId, bannerData, navigate) => {
  logger.info("Banner data received to update:", { data: bannerData });

  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(
      bannerData.isNewBannerImageUploaded
        ? {
            redirectUrl: bannerData.redirectUrl,
            bannerImageFileName: bannerData.bannerImageFileName,
          }
        : { redirectUrl: bannerData.redirectUrl },
    ),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}banner/${bannerId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await updateBanner(bannerId, bannerData, navigate);
    }

    return data;
  } catch (error) {
    logger.error("Error updating banner:", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Deletes a banner by its identifier.
 * @async
 * @param {string} bannerId - The banner's unique identifier
 * @param {Function} navigate - React Router navigation function for redirect on auth failure
 * @returns {Promise<boolean>} True if deletion was successful, false otherwise
 */
export const deleteBanner = async (bannerId, navigate) => {
  const options = {
    method: "DELETE",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}banner/${bannerId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await deleteBanner(bannerId, navigate);
    }

    return response.ok;
  } catch (error) {
    logger.error("Error deleting banner:", { err: sanitizeError(error) });
    return false;
  }
};
